import {
  DAY_LABEL,
  DEFAULT_FROM,
  DEFAULT_TO,
  MIN_GROUP_LENGTH,
  TIME_ERRORS,
  TIME_RE,
} from "./constants";
import { ScheduleDay, ScheduleType, ScheduleValue, TimeField } from "./types";

export const isValidTime = (time = "", field: TimeField = "to") => {
  const t = time.trim();

  if (!t) {
    return true;
  }

  if (field === "from" && t === DEFAULT_TO) {
    return false;
  }

  return TIME_RE.test(t);
};

export const isValidRange = (from = "", to = "") => {
  const f = from.trim() || DEFAULT_FROM;
  const t = to.trim() || DEFAULT_TO;

  if (!isValidTime(f, "from") || !isValidTime(t, "to")) {
    return true;
  }

  return f < t;
};

export const isBothOrNone = (from = "", to = "") => !from.trim() === !to.trim();

export const getTimeRangeError = (from = "", to = ""): string | undefined => {
  if (!isValidTime(from, "from")) {
    return TIME_ERRORS.from;
  }

  if (!isValidTime(to, "to")) {
    return TIME_ERRORS.to;
  }

  if (!isBothOrNone(from, to)) {
    return TIME_ERRORS.bothOrNone;
  }

  if (!isValidRange(from, to)) {
    return TIME_ERRORS.range;
  }

  return undefined;
};

export const isValidTimeRange = (from = "", to = "") =>
  !getTimeRangeError(from, to);

export const isWholeDay = (from = "", to = "") => {
  const f = from.trim();
  const t = to.trim();

  return (!f && !t) || (f === DEFAULT_FROM && t === DEFAULT_TO);
};

export const normalizeDay = (day: ScheduleDay): ScheduleDay => {
  if (isWholeDay(day.from, day.to)) {
    return { value: day.value };
  }

  const result: ScheduleDay = { value: day.value };

  if (day.from) {
    result.from = day.from;
  }

  if (day.to) {
    result.to = day.to;
  }

  return result;
};

export const buildValue = (
  type: ScheduleType,
  days: ScheduleDay[]
): ScheduleValue => ({ type, days });

export const getDayLabel = (type: ScheduleType, dayVal: number) =>
  type === "week" ? DAY_LABEL.get(dayVal) ?? String(dayVal) : String(dayVal);

const groupConsecutive = (values: number[]): [number, number][] => {
  const sorted = Array.from(new Set(values)).sort((a, b) => a - b);
  const groups: [number, number][] = [];

  sorted.forEach((v) => {
    const last = groups[groups.length - 1];

    if (last && v === last[1] + 1) {
      last[1] = v;
    } else {
      groups.push([v, v]);
    }
  });

  return groups;
};

export const formatDayGroups = (type: ScheduleType, values: number[]) =>
  groupConsecutive(values).flatMap(([start, end]) => {
    const length = end - start + 1;

    if (length >= MIN_GROUP_LENGTH) {
      return [`${getDayLabel(type, start)} – ${getDayLabel(type, end)}`];
    }

    return Array.from({ length }, (_, i) => getDayLabel(type, start + i));
  });

export const getCommonRange = (
  days: ScheduleDay[]
): { from: string; to: string } | null => {
  if (!days.length || !days.every((d) => isValidTimeRange(d.from, d.to))) {
    return null;
  }

  const ranges = days.map((d) => ({
    from: d.from?.trim() || DEFAULT_FROM,
    to: d.to?.trim() || DEFAULT_TO,
  }));

  const [first] = ranges;
  const same = ranges.every((r) => r.from === first.from && r.to === first.to);

  return same ? first : null;
};
